import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import { FastField, Field, Formik } from 'formik';
import KTFormGroup from 'general/components/OtherKeenComponents/Forms/KTFormGroup';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import InputSearchDropdown from 'general/components/Others/InputSearchDropdown';
import AppColors from 'general/constants/AppColors';
import agencyBankHelper from 'general/helpers/agencyBankHelper';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

DialogAddBankAccountView.propTypes = {
    agencyId: PropTypes.number,
    item: PropTypes.object,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogAddBankAccountView({ agencyId, item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);
    const isMaintainer = user?.role === 'MAINTAINER';
    const editMode = !!item;
    const refFormik = useRef(null);
    const initialFormikValues = useMemo(() => {
        return {
            bankCode: item?.bankCode ?? '',
            bankAccount: item?.bankAccount ?? '',
            bankBeneficiary: item?.bankBeneficiary ?? '',
        };
    }, [item]);
    const [loading, setLoading] = useState(false);
    const [bankList, setBankList] = useState([]);

    useEffect(() => {
        handleGetBankList();
    }, []);

    // MARK: ---- METHODS ----

    const handleGetBankList = async () => {
        const banks = await miscHelper.callApiGetBankList();
        const bankList = banks?.map((bank) => {
            return { bankCode: bank?.bankCode, shortName: bank?.shortName, bankName: bank?.bankName };
        });
        setBankList(bankList);
    };

    const [qBank, setQBank] = useState('');
    const bankOptions = useMemo(() => {
        if (!bankList || !Array.isArray(bankList)) {
            return [];
        }

        const filteredOptions = qBank
            ? bankList.filter((item) => {
                const bankCode = StringUtils.removeVietnameseTones(
                    item?.bankCode ?? ''
                ).toLowerCase();
                const shortName = StringUtils.removeVietnameseTones(
                    item?.shortName ?? ''
                ).toLowerCase();
                const bankName = StringUtils.removeVietnameseTones(
                    item?.bankName ?? ''
                ).toLowerCase();
                const searchTerm = StringUtils.removeVietnameseTones(
                    qBank ?? ''
                ).toLowerCase();
                return bankCode.includes(searchTerm) || shortName.includes(searchTerm) || bankName.includes(searchTerm);
            })
            : bankList;

        return filteredOptions.map((row) => ({
            title: row?.shortName?.length ? `${row?.shortName ?? ''}, ${row?.bankCode ?? ''}` : row?.bankCode ?? '',
            subTitle: row?.bankName ?? '',
            value: row?.bankCode ?? ''
        }));
    }, [bankList, qBank]);

    const handleSubmit = async (values) => {
        setLoading(true);
        console.log(values);
        const bankName = values.bankCode
            ? bankList?.find((bank) => bank.bankCode === values.bankCode)?.bankName
            : '';
        try {
            if (!editMode) {
                if (isMaintainer) {
                    const res = await MaintainerHelper.API.requestCreateMaintainerBank(
                        Utils.transformEmptyStrings({
                            bankName: bankName,
                            bankCode: values.bankCode,
                            bankAccount: values.bankAccount,
                            bankBeneficiary: values.bankBeneficiary,
                            agencyId: agencyId,
                        })
                    );
                    if (res && _.isObject(res)) {
                        ToastHelper.showSuccess('Thêm tài khoản ngân hàng thành công');
                        if (onDone) {
                            onDone();
                        }
                    } else if (_.isString(res)) {
                        ToastHelper.showError(res);
                        return;
                    }
                } else {
                    const { result } = await agencyBankHelper.callApiCreateAgencyBank(
                        Utils.transformEmptyStrings({
                            bankName: bankName,
                            bankCode: values.bankCode,
                            bankAccount: values.bankAccount,
                            bankBeneficiary: values.bankBeneficiary,
                            agencyId: agencyId,
                        })
                    );
                    if (result === 'success') {
                        ToastHelper.showSuccess('Thêm tài khoản ngân hàng thành công');
                        if (onDone) {
                            onDone();
                        }
                    } else if (_.isString(result)) {
                        ToastHelper.showError(result);
                        return;
                    }
                }
            } else {
                let response;
                if (isMaintainer) {
                    response = await MaintainerHelper.API.requestUpdateMaintainerBank(
                        item?.bankAccountId,
                        Utils.transformEmptyStrings({
                            bankName: bankName,
                            bankCode: values.bankCode,
                            bankAccount: values.bankAccount,
                            bankBeneficiary: values.bankBeneficiary,
                        })
                    );
                } else {
                    response = await agencyBankHelper.callApiUpdateAgencyBank(
                        item?.bankAccountId,
                        Utils.transformEmptyStrings({
                            bankName: bankName,
                            bankCode: values.bankCode,
                            bankAccount: values.bankAccount,
                            bankBeneficiary: values.bankBeneficiary,
                        })
                    );
                }
                if (_.isBoolean(response) && response) {
                    ToastHelper.showSuccess('Cập nhật tài khoản ngân hàng thành công');
                    if (onDone) {
                        onDone();
                    }
                } else if (_.isString(response)) {
                    ToastHelper.showError(response);
                    return;
                }
            }
        } catch (error) { }
        setLoading(false);
    };

    const [looking, setLooking] = useState(false);
    const handleLookupAgencyBank = async () => {
        if (looking) return;

        setLooking(true);
        const values = refFormik.current.values;

        const payload = Utils.transformEmptyStrings({
            bankCode: values.bankCode,
            bankAccount: values.bankAccount,
        });

        const res = await agencyBankHelper.callApiLookupAgencyBank(payload);

        if (res && _.isObject(res) && res.accountName) {
            refFormik.current.setFieldValue('bankBeneficiary', res.accountName);
        } else {
            ToastHelper.showError(res || 'Không tìm thấy kết quả');
        }
        setLooking(false);
    };

    return (
        <div>
            <Formik
                initialValues={initialFormikValues}
                enableReinitialize
                onSubmit={(values) => {
                    handleSubmit(values);
                }}
                validationSchema={Yup.object({
                    bankCode: Yup.string().required(t('required')),
                    bankAccount: Yup.string().required(t('required')),
                    bankBeneficiary: Yup.string().required(t('required')),
                })}
            >
                {(formikProps) => {
                    refFormik.current = formikProps;
                    return (
                        <div className="d-flex flex-column">
                            <div className="row m-0 p-0 px-3 mt-4">
                                <KTFormGroup
                                    label={
                                        <span
                                            className="font-weight-bolder"
                                            style={{
                                                color: AppColors.gray[800],
                                                fontSize: Utils.returnFontSize(13),
                                                fontWeight: 700,
                                            }}
                                        >
                                            {t('Ngân hàng')}{' '}
                                        </span>
                                    }
                                    inputName="bankCode"
                                    additionalClassName=" mb-4 px-1"
                                    inputElement={
                                        <Field name="bankCode">
                                            {({ field, form, meta }) => {
                                                const selectedBank = bankOptions?.find(bank => bank.value === field.value);
                                                return (
                                                    <InputSearchDropdown
                                                        inputName={field.name}
                                                        selectedValue={field.value}
                                                        onSelectedValueChanged={(value) => {
                                                            form.setFieldValue(field.name, value);
                                                            setQBank('');
                                                        }}
                                                        items={bankOptions}
                                                        onSubmitSearch={(text) => {
                                                            setQBank(text);
                                                        }}
                                                        selectedText={selectedBank ? `${selectedBank?.subTitle} (${selectedBank?.value})` : qBank}
                                                        // disabled={isEditMode}
                                                        enableCheckValid={true}
                                                        isTouched={meta.touched}
                                                        isValid={_.isEmpty(meta.error)}
                                                        onBlur={() => {
                                                            form.setFieldTouched(field.name, true);
                                                        }}
                                                        placeholder='Theo tên ngân hàng, tên rút gọn'
                                                    />
                                                );
                                            }}
                                        </Field>
                                    }
                                    isRequired={true}
                                />
                            </div>
                            <div className="row m-0 mb-4 p-0 px-3 d-flex align-items-end">
                                <KTFormGroup
                                    label={
                                        <span
                                            className="font-weight-bolder"
                                            style={{
                                                color: AppColors.gray[800],
                                                fontSize: Utils.returnFontSize(13),
                                                fontWeight: 700,
                                            }}
                                        >
                                            {t('Số tài khoản ngân hàng')}{' '}
                                        </span>
                                    }
                                    inputName="bankAccount"
                                    additionalClassName="mb-0 px-1"
                                    inputElement={
                                        <Field name="bankAccount">
                                            {({ field, form, meta }) => {
                                                return (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => {
                                                            form.setFieldValue(field.name, value);
                                                        }}
                                                        onBlur={() => {
                                                            form.setFieldTouched(field.name, true);
                                                        }}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={t('Nhập số tài khoản')}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                        additionalButton={(
                                                            <button
                                                                style={{
                                                                    ...UIHelper.buildFontStyle('600', '13px', '#fff'),
                                                                    backgroundColor: AppColors.info,
                                                                    border: 'none',
                                                                    padding: '6px 16px',
                                                                    borderRadius: '6px',
                                                                    cursor: looking ? 'not-allowed' : 'pointer',
                                                                    opacity: looking ? 0.7 : 1,
                                                                    minWidth: '100px',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '8px',
                                                                }}
                                                                onClick={handleLookupAgencyBank}
                                                                disabled={looking}
                                                            >
                                                                {looking ? (
                                                                    <>
                                                                        Tra cứu
                                                                        <span className="spinner spinner-white mr-4"></span>
                                                                    </>
                                                                ) : (
                                                                    'Tra cứu'
                                                                )}
                                                            </button>
                                                        )}
                                                    />
                                                );
                                            }}
                                        </Field>
                                    }
                                    isRequired={true}
                                />
                            </div>

                            <div className="row m-0 p-0 px-3">
                                <KTFormGroup
                                    label={<span className="font-weight-bolder" style={{
                                        color: AppColors.gray[800],
                                        fontSize: Utils.returnFontSize(13),
                                        fontWeight: 700
                                    }}>{t('Tên chủ tài khoản ngân hàng')} </span>}
                                    inputName="bankBeneficiary"
                                    additionalClassName="mb-4 px-1"
                                    inputElement={
                                        <FastField name="bankBeneficiary">
                                            {({ field, form, meta }) => {
                                                return (
                                                    <KTFormInput
                                                        name={field.name}
                                                        value={field.value}
                                                        onChange={(value) => {
                                                            let filteredValue = StringUtils.removeVietnameseTones(value);
                                                            filteredValue = filteredValue.toUpperCase();
                                                            filteredValue = filteredValue.replace(/[^A-Z0-9 ,.-]/g, '');
                                                            form.setFieldValue(field.name, filteredValue);
                                                        }}
                                                        onBlur={() => {
                                                            form.setFieldTouched(field.name, true);
                                                        }}
                                                        enableCheckValid
                                                        type={KTFormInputType.text}
                                                        placeholder={t('Nhập họ và tên IN HOA, không dấu')}
                                                        isValid={_.isEmpty(meta.error)}
                                                        isTouched={meta.touched}
                                                        feedbackText={meta.error}
                                                    />
                                                );
                                            }}
                                        </FastField>
                                    }
                                    isRequired={true}
                                />
                            </div>
                        </div>
                    );
                }}
            </Formik>

            {/* footer */}
            <div
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                <Button
                    variant="secondary"
                    className="fw-bold w-50"
                    style={{
                        fontSize: '13px',
                        color: AppColors.gray[800],
                        padding: '8px 16px',
                    }}
                    onClick={onCancel}
                >
                    {t('Hủy bỏ')}
                </Button>

                <Button
                    variant="success"
                    className="fw-bold w-50"
                    style={{
                        fontSize: '14px',
                        padding: '8px 16px',
                    }}
                    onClick={() => refFormik.current.handleSubmit()}
                >
                    {t('Lưu lại')}
                </Button>
            </div>
        </div >
    );
}

export default DialogAddBankAccountView;
